<template>
  <div class="articles-page">

    <!-- Page header -->
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <div class="page-title-bar"></div>
    </div>

    <!-- Error -->
    <v-container v-if="errors.length">
      <v-alert type="error" variant="tonal" v-for="(error, i) in errors" :key="i">{{ error }}</v-alert>
    </v-container>

    <!-- Empty state -->
    <v-container v-if="!articles.length && !errors.length" class="text-center py-16">
      <v-icon size="48" color="grey-lighten-1">mdi-newspaper-variant-outline</v-icon>
      <p class="mt-4 text-grey">Aucune publication pour le moment.</p>
    </v-container>

    <!-- Articles list -->
    <v-container class="articles-container">
      <v-card
        v-for="(item, index) in articles"
        :key="'post' + index"
        class="article-card"
        elevation="1"
        rounded="lg"
      >
        <v-row no-gutters>
          <!-- Image -->
          <v-col cols="12" sm="3" v-if="item.picture">
            <v-img
              :src="item.picture"
              :key="componentKey"
              height="100%"
              min-height="160"
              cover
              class="article-image"
            ></v-img>
          </v-col>

          <!-- Content -->
          <v-col>
            <div class="article-content">
              <div class="article-meta">
                <v-icon size="14" color="grey">mdi-calendar-outline</v-icon>
                <span class="article-date">{{ item.date }}</span>
                <v-chip v-if="item.eventtype" size="x-small" color="orange-lighten-4" class="ml-2">
                  {{ item.eventtype }}
                </v-chip>
              </div>
              <h2 class="article-title">{{ item.title }}</h2>
              <p class="article-description">{{ item.description }}</p>
              <div v-if="item.docpath" class="mt-3">
                <v-btn
                  @click="getDoc(item.docpath)"
                  size="small"
                  variant="outlined"
                  color="#E65100"
                  prepend-icon="mdi-file-pdf-box"
                >
                  Voir le document
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

  </div>
</template>

<script>
export default {
  props: ['article_type'],
  data () {
    return {
      articles: [],
      errors: [],
      componentKey: 0
    }
  },
  computed: {
    pageTitle () {
      const titles = {
        Press: 'Presse',
        News: 'Actualité',
        Meeting: 'Évènements',
        Publication: 'Publications',
        Members: 'Espace Membres',
      }
      return titles[this.article_type] || 'Articles'
    }
  },
  created () {
    this.getEvents()
  },
  watch: {
    '$route' () {
      this.errors = []
      this.componentKey = 0
      this.getEvents()
    }
  },
  methods: {
    getDoc (filename) {
      this.$apiclient.getDoc(filename)
        .then(response => { this.downloadFile(response, 'document') })
        .catch(() => { console.warn('error from getDoc') })
    },
    downloadFile (response, filename) {
      const newBlob = new Blob([response.data], { type: 'application/pdf' })
      const data = window.URL.createObjectURL(newBlob)
      const link = document.createElement('a')
      link.href = data
      link.target = '_blank'
      link.download = filename + '.pdf'
      link.click()
      setTimeout(() => { window.URL.revokeObjectURL(data) }, 100)
    },
    getPicture (pPath, id) {
      this.$apiclient.getPicture(pPath)
        .then(response => {
          this.componentKey += 1
          const bytes = new Uint8Array(response.data)
          let binary = ''
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
          const img = btoa(binary)
          this.articles[id].picture = 'data:' + response.headers['content-type'] + ';base64,' + img
        })
        .catch(error => {
          if (error.response === undefined) {
            this.errors.push('Erreur de connexion')
          } else if (error.response.status === 401) {
            this.errors.push("Vous n'êtes pas connecté ou non authorisé")
          } else {
            this.errors.push(error.response.statusText)
          }
        })
    },
    getPictures () {
      for (var i = 0; i < this.articles.length; i++) {
        if (this.articles[i].picturepath) this.getPicture(this.articles[i].picturepath, i)
      }
    },
    getEvents () {
      this.$apiclient.getEventsByType(this.article_type)
        .then(response => {
          if (response.status !== 200) this.errors.push(response.statusText)
          this.articles = (response.data != null) ? response.data : []
          this.getPictures()
        })
        .catch(error => {
          if (error.response === undefined) {
            this.errors.push('Erreur de connexion')
          } else if (error.response.status === 401) {
            this.errors.push("Vous n'êtes pas connecté ou non authorisé")
          } else {
            this.errors.push(error.response.statusText)
          }
        })
    }
  }
}
</script>

<style scoped>
.articles-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-header {
  padding: 32px 24px 20px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  text-align: center;
}

.page-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #212121;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.page-title-bar {
  width: 40px;
  height: 3px;
  background: #E65100;
  margin: 0 auto;
  border-radius: 2px;
}

.articles-container {
  max-width: 860px;
  padding-top: 24px;
  padding-bottom: 40px;
}

.article-card {
  margin-bottom: 16px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.article-card:hover {
  box-shadow: 0 4px 20px rgba(0,0,0,0.1) !important;
}

.article-image {
  border-radius: 0;
}

.article-content {
  padding: 20px 24px;
}

.article-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
}

.article-date {
  font-size: 0.75rem;
  color: #757575;
  margin-left: 4px;
}

.article-title {
  font-size: 1rem;
  font-weight: 700;
  color: #212121;
  margin-bottom: 10px;
  line-height: 1.4;
}

.article-description {
  font-size: 0.87rem;
  color: #555555;
  line-height: 1.7;
  margin: 0;
}
</style>
